import { ReturnSourceContext } from "@api/sharing/ReturnProductManagementApi";
import { fetchSourceOrderProductList, SourceOrderProductItem } from "@redux/sharing/ReturnProductManagementSlice";
import formatEffectiveDateBatch from "@utils/formatEffectiveDateBatch";
import { useAppDispatch } from "src/store";

import React, { useEffect, useMemo, useState } from "react";
import { Button, Checkbox, Col, InputNumber, message, Row, Select, Skeleton, Space, Table, Tabs } from "antd";
import { ColumnsType } from "antd/lib/table";
import styled from "styled-components";

export interface Step2ProductRow extends SourceOrderProductItem {
  checked: boolean;
  returnQty: number;
}

const Wrapper = styled.div`
  max-width: 960px;
`;
const EffectiveDateText = styled.span`
  color: #999;
`;
const FooterRow = styled(Row)`
  margin-top: 16px;
`;
const SearchRow = styled(Row)`
  margin-bottom: 16px;
`;

type Props = {
  sourceContext: ReturnSourceContext;
  productRows: Step2ProductRow[];
  onChangeProductRows: (rows: Step2ProductRow[]) => void;
  onBackStep: () => void;
  onNextStep: () => void;
};

export default function ReturnProductAppCreateStep2(props: Props) {
  const { sourceContext, productRows, onChangeProductRows, onBackStep, onNextStep } = props;
  const dispatch = useAppDispatch();
  const [isFetching, setIsFetching] = useState(false);
  const [activeTab, setActiveTab] = useState<"selected" | "unselected">("selected");
  const [pendingSkus, setPendingSkus] = useState<string[]>([]);
  const [appliedSkus, setAppliedSkus] = useState<string[]>([]);

  const handleSearch = () => {
    setAppliedSkus(pendingSkus);
  };
  const handleShowAll = () => {
    setPendingSkus([]);
    setAppliedSkus([]);
  };

  useEffect(() => {
    if (productRows.length > 0) return;
    setIsFetching(true);
    dispatch(fetchSourceOrderProductList(sourceContext))
      .unwrap()
      .then((items) => {
        onChangeProductRows(items.map((item) => ({ ...item, checked: true, returnQty: item.originalQty })));
      })
      .finally(() => setIsFetching(false));
    // 只在 Step2 首次進畫面時撈取一次，之後切換頁籤/勾選不重撈
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const searchedRows = useMemo(() => {
    if (appliedSkus.length === 0) return productRows;
    return productRows.filter((row) => row.sku !== null && appliedSkus.includes(row.sku));
  }, [productRows, appliedSkus]);

  const skuSelectOptions = productRows
    .filter((row): row is Step2ProductRow & { sku: string } => row.sku !== null)
    .map((row) => ({
      value: row.sku,
      sku: row.sku,
      productName: row.productName,
      label: (
        <Space size={4}>
          <strong>[{row.sku}]</strong>
          <span>{row.productName}</span>
          <EffectiveDateText>{formatEffectiveDateBatch(row.effectiveDate, row.batchNumber)}</EffectiveDateText>
        </Space>
      ),
    }));

  const selectedRows = searchedRows.filter((row) => row.checked);
  const unselectedRows = searchedRows.filter((row) => !row.checked);
  const visibleRows = activeTab === "selected" ? selectedRows : unselectedRows;
  const allVisibleChecked = visibleRows.length > 0 && visibleRows.every((row) => row.checked);

  const handleNextStep = () => {
    if (selectedRows.length === 0) {
      message.error("請至少選擇一項退貨商品");
      return;
    }
    onNextStep();
  };

  const updateRow = (productId: string, updates: Partial<Step2ProductRow>) => {
    onChangeProductRows(productRows.map((row) => (row.productId === productId ? { ...row, ...updates } : row)));
  };

  const handleToggleAllVisible = (checked: boolean) => {
    const visibleProductIdSet = new Set(visibleRows.map((row) => row.productId));
    onChangeProductRows(productRows.map((row) => (visibleProductIdSet.has(row.productId) ? { ...row, checked } : row)));
  };

  const columns: ColumnsType<Step2ProductRow> = [
    {
      title: <Checkbox checked={allVisibleChecked} onChange={(e) => handleToggleAllVisible(e.target.checked)} />,
      dataIndex: "checked",
      width: 60,
      render: (checked: boolean, record) => (
        <Checkbox checked={checked} onChange={(e) => updateRow(record.productId, { checked: e.target.checked })} />
      ),
    },
    {
      title: "SKU / 商品名稱",
      dataIndex: "sku",
      render: (sku: string | null, record) => (
        <span>
          {sku ?? "-"} / {record.productName}
        </span>
      ),
    },
    {
      title: "效期_批號",
      dataIndex: "batchNumber",
      render: (batchNumber: string | null, record) => formatEffectiveDateBatch(record.effectiveDate, batchNumber),
    },
    {
      title: "退貨數量",
      dataIndex: "returnQty",
      render: (returnQty: number, record) => (
        <Space>
          <InputNumber
            min={0}
            max={record.originalQty}
            value={returnQty}
            onChange={(value) => updateRow(record.productId, { returnQty: Number(value) || 0 })}
          />
          <span>/ 原始 {record.originalQty}</span>
        </Space>
      ),
    },
  ];

  return (
    <Wrapper>
      <SearchRow gutter={8} align="middle">
        <Col span={14}>
          <Select
            mode="multiple"
            showSearch
            allowClear
            style={{ width: "100%" }}
            placeholder="可輸入1個以上的 SKU 或商品名稱進行查詢"
            value={pendingSkus}
            onChange={(skuList: string[]) => setPendingSkus(skuList)}
            optionLabelProp="value"
            filterOption={(input, option) => {
              const keyword = input.trim();
              return Boolean(option?.sku.includes(keyword) || option?.productName.includes(keyword));
            }}
            options={skuSelectOptions}
          />
        </Col>
        <Col>
          <Button onClick={handleSearch}>查詢</Button>
        </Col>
        <Col>
          <Button onClick={handleShowAll}>顯示全部商品</Button>
        </Col>
      </SearchRow>
      <Tabs
        activeKey={activeTab}
        onChange={(key) => setActiveTab(key as "selected" | "unselected")}
        items={[
          { key: "selected", label: `已選擇退貨 (${selectedRows.length})` },
          { key: "unselected", label: `不退貨商品 (${unselectedRows.length})` },
        ]}
      />
      {isFetching ? (
        <Skeleton active />
      ) : (
        <Table rowKey="productId" columns={columns} dataSource={visibleRows} pagination={false} />
      )}
      <FooterRow justify="end">
        <Space>
          <Button onClick={onBackStep}>返回</Button>
          <Button type="primary" onClick={handleNextStep}>
            儲存此分頁
          </Button>
        </Space>
      </FooterRow>
    </Wrapper>
  );
}
